import { Button, IconButton, Stack } from '@mui/material';
import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client';
import type { DeepPartial } from '@apollo/client/utilities';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { useTranslation } from 'react-i18next';
import { useSignOut } from 'src/api/provider';
import { Maybe } from 'src/graphql/lib';
import { CloseIcon } from 'src/icons';
import { getBrowserTimeZone } from 'src/lib/browserTimeZone';
import Cookies from 'universal-cookie';
import { EMPTY, from, mergeMap, throwError } from 'rxjs';
import 'whatwg-fetch';
import {
    API_HOST,
    CSRF_COOKIE_NAME,
    GRAPHQL_CONSISTENCY_TOKENS_ENABLED,
} from '../constants/environment';
import { IMPERSONATED_KEY } from '../constants/sessionStorage';
import { safeSessionStorage } from 'src/lib/browserStorage';
import {
    applyAppActiveContextHeader,
    isInvalidAppActiveContextGraphqlError,
    recoverInvalidAppActiveContext,
} from '../api/appActiveContext';
import {
    getGraphQLValidationErrorMessages,
    isAbortError,
    isStaleClientGraphQLResponseBody,
    isStaleClientGraphQLError,
    type GraphQLErrorLike,
} from './errorHandling';
import {
    CONSISTENCY_TOKEN_HEADER,
    getConsistencyTokenHeaderValue,
    syncConsistencyTokensFromHeaders,
} from './graphqlConsistency';

declare module '@apollo/client' {
    namespace ApolloClient {
        namespace DeclareDefaultOptions {
            interface WatchQuery {
                errorPolicy?: 'all';
            }
        }
    }
}

type StatusCodeError = Error | { statusCode?: number } | null | undefined;

const hasStatusCode = (value: StatusCodeError): value is { statusCode: number } =>
    typeof value === 'object' &&
    value !== null &&
    'statusCode' in value &&
    typeof value.statusCode === 'number';

const hasInvalidHeaderChars = (value: string) => /[\r\n]/.test(value);

const toSafeHeaderValue = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const valueString = String(value);
    if (!valueString || hasInvalidHeaderChars(valueString)) {
        return null;
    }
    return valueString;
};

const toSafeHeaders = (headers: unknown): Record<string, string> => {
    // Validate using the native Headers API, but return a plain object.
    // Apollo HttpLink merges context headers as plain objects; returning a Headers
    // instance here can break that merge behavior and lead to missing auth/CSRF
    // headers (we saw /api/graphql/ 403s when returning Headers directly).
    const validatedHeaders = new Headers();
    const appendHeader = (name: unknown, value: unknown) => {
        if (typeof name !== 'string' || !name) {
            return;
        }
        const safeValue = toSafeHeaderValue(value);
        if (!safeValue) {
            return;
        }
        try {
            validatedHeaders.set(name, safeValue);
        } catch {
            // Ignore invalid header names/values.
        }
    };

    if (!headers) {
        return {};
    }

    if (headers instanceof Headers) {
        headers.forEach((value, key) => appendHeader(key, value));
    } else if (Array.isArray(headers)) {
        headers.forEach((entry) => {
            if (Array.isArray(entry) && entry.length === 2) {
                appendHeader(entry[0], entry[1]);
            }
        });
    } else if (typeof headers === 'object') {
        Object.entries(headers as Record<string, unknown>).forEach(([name, value]) => {
            appendHeader(name, value);
        });
    }

    const safeHeaders: Record<string, string> = {};
    validatedHeaders.forEach((value, key) => {
        safeHeaders[key] = value;
    });
    return safeHeaders;
};
const cookies = new Cookies();

type RefreshTokenResponse = {
    access?: string;
};

type RefreshResult = {
    ok: boolean;
};

let refreshInFlight: Promise<RefreshResult> | null = null;
const refreshSession = async (): Promise<RefreshResult> => {
    try {
        const headers: Record<string, string> = {};
        const csrfToken = cookies.get(CSRF_COOKIE_NAME);
        if (typeof csrfToken === 'string' && csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        }
        const response = await fetch(`${API_HOST}/api/auth/token/refresh/`, {
            method: 'POST',
            credentials: 'include',
            headers,
        });
        if (!response.ok) {
            return { ok: false };
        }

        await response.json().catch(() => ({}) as RefreshTokenResponse);
        return { ok: true };
    } catch {
        return { ok: false };
    }
};

const ensureFreshSession = (): Promise<RefreshResult> => {
    if (!refreshInFlight) {
        refreshInFlight = refreshSession().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

function useApolloClient(
    networkStatusNotifierLink: ReturnType<typeof createNetworkStatusNotifier>['link'],
    cacheScopeKey: string,
) {
    const { t } = useTranslation('validation');
    const { closeSnackbar, enqueueSnackbar } = useSnackbar();
    const signOut = useSignOut();

    const showError = useCallback(
        (message: string | string[]) => {
            if (Array.isArray(message)) {
                message = message.join(' ');
            }
            enqueueSnackbar(message, { variant: 'error' });
        },
        [enqueueSnackbar],
    );

    const showStaleClientRefreshPrompt = useCallback(() => {
        enqueueSnackbar(t('staleClientRefreshMessage'), {
            variant: 'warning',
            preventDuplicate: true,
            persist: true,
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            },
            action: (key) => (
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                        color="inherit"
                        size="small"
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1.25,
                            minWidth: 'auto',
                            padding: 0,
                            textTransform: 'none',
                        }}
                        onClick={() => window.location.reload()}
                    >
                        {t('staleClientRefreshButtonLabel')}
                    </Button>
                    <IconButton
                        aria-label="Close"
                        color="inherit"
                        size="small"
                        sx={{ padding: 0 }}
                        onClick={() => closeSnackbar(key)}
                    >
                        <CloseIcon height={12} width={12} />
                    </IconButton>
                </Stack>
            ),
        });
    }, [closeSnackbar, enqueueSnackbar, t]);

    // Error messages follow the current language without replacing the client/cache.
    // Publish callbacks after commit so requests never observe an abandoned render.
    const errorHandlers = useRef({ t, showError, showStaleClientRefreshPrompt });
    useLayoutEffect(() => {
        errorHandlers.current = { t, showError, showStaleClientRefreshPrompt };
    }, [t, showError, showStaleClientRefreshPrompt]);

    // Keep useMemo here - ApolloClient is a third-party library that requires stable reference
    const apolloClient = useMemo(() => {
        const graphqlFetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
            const response = await window.fetch(input, init);
            if (GRAPHQL_CONSISTENCY_TOKENS_ENABLED) {
                syncConsistencyTokensFromHeaders(response.headers);
            }
            return response;
        }) as typeof window.fetch;

        const cache = new InMemoryCache({
            possibleTypes: {},
            typePolicies: {
                // === Types without ID fields that return signed URLs ===
                // These types have no id field and return URLs with changing expiration
                // timestamps. Without these policies, Apollo sees each URL as "new data"
                // and triggers infinite refetches.
                ImageType: {
                    keyFields: false,
                    merge: true,
                },
                FileType: {
                    keyFields: false,
                    merge: true,
                },

                // === Types with 'key' field that can be used as identifier ===
                // These types have a stable 'key' field that can identify them uniquely.
                LanguageType: {
                    keyFields: ['key'],
                },

                // === Profile types fetched with different field selections ===
                // These types are commonly fetched from multiple queries with different
                // fields. Without merge: true, Apollo would lose fields between queries.
                VendorProfileNode: {
                    merge: true,
                },
                UserProfileNode: {
                    merge: true,
                },
                VendorPerformanceProfile: {
                    keyFields: false,
                    merge: true,
                },
                VendorBusinessProfileNode: {
                    merge: true,
                },

                // === Nested types commonly causing merge conflicts ===
                // These embedded types don't have IDs and are queried with different
                // field selections from different parts of the app.
                VendorScoreSummary: {
                    keyFields: false,
                    merge: true,
                },
                VendorPerformancePillarPercentiles: {
                    keyFields: false,
                    merge: true,
                },
                ReviewMetrics: {
                    keyFields: false,
                    merge: true,
                },
                LeadInboundRequestMatchInfoType: {
                    keyFields: false,
                    merge: true,
                },
                LeadInboundRequestConversionInfoType: {
                    keyFields: false,
                    merge: true,
                },
            },
        });

        const httpLink = new HttpLink({
            uri: `${API_HOST}/api/graphql/`,
            credentials: 'include',
            fetch: graphqlFetch,
        });

        const customHeadersLink = setContext((_, { headers }) => {
            const safeHeaders = toSafeHeaders(headers);
            const csrfToken = toSafeHeaderValue(cookies.get(CSRF_COOKIE_NAME));
            if (csrfToken) {
                safeHeaders['X-CSRFToken'] = csrfToken;
            }
            const impersonated = toSafeHeaderValue(safeSessionStorage.getItem(IMPERSONATED_KEY));
            if (impersonated) {
                safeHeaders['X-Impersonate'] = impersonated;
            }
            applyAppActiveContextHeader(safeHeaders);
            const browserTimeZone = toSafeHeaderValue(getBrowserTimeZone());
            if (browserTimeZone) {
                safeHeaders.TZ = browserTimeZone;
            }
            let consistencyTokens: string | null = null;
            if (GRAPHQL_CONSISTENCY_TOKENS_ENABLED) {
                try {
                    consistencyTokens = toSafeHeaderValue(getConsistencyTokenHeaderValue());
                } catch (error) {
                    console.warn('Failed to read request consistency tokens.', error);
                }
            }
            if (consistencyTokens) {
                safeHeaders[CONSISTENCY_TOKEN_HEADER] = consistencyTokens;
            }

            return {
                headers: safeHeaders,
                fetchOptions: {
                    referrerPolicy: 'strict-origin-when-cross-origin',
                },
            };
        });

        const sentryBreadcrumbLink = new ApolloLink((operation, forward) => {
            Sentry.addBreadcrumb({
                category: 'gql',
                level: 'info',
                message: `Operation: ${operation.operationName}`,
            });

            return forward?.(operation) ?? null;
        });

        const errorLink = onError(({ error, operation, forward }) => {
            const { t, showError, showStaleClientRefreshPrompt } = errorHandlers.current;
            if (!error) {
                return;
            }

            // Fetch cancellation is expected when Apollo supersedes a request or its route unmounts.
            // Complete the observable so it cannot surface a false error toast or unhandled rejection.
            if (isAbortError(error)) {
                return EMPTY;
            }

            if (CombinedGraphQLErrors.is(error)) {
                if (
                    error.errors.some((graphQLError) =>
                        isStaleClientGraphQLError(graphQLError as GraphQLErrorLike),
                    )
                ) {
                    showStaleClientRefreshPrompt();
                    return;
                }

                error.errors.forEach((graphQLError) => {
                    const typedGraphQLError = graphQLError as GraphQLErrorLike;
                    if (!graphQLError.extensions) {
                        showError(graphQLError.message);
                        return;
                    }
                    switch (graphQLError.extensions.code) {
                        case 'ValidationError':
                            const errorMessages = getGraphQLValidationErrorMessages(
                                typedGraphQLError.extensions?.fields,
                            );
                            showError(
                                errorMessages.length > 0
                                    ? errorMessages
                                    : t('validationErrorMessage'),
                            );
                            break;
                        case 'AuthorizationError':
                            showError(t('unauthorizedErrorMessage'));
                            break;
                        case 'InternalServerError':
                        default:
                            showError(graphQLError.message);
                    }
                });
                return;
            }

            const operationName = operation.operationName ?? '';

            if (ServerError.is(error) && isInvalidAppActiveContextGraphqlError(error)) {
                recoverInvalidAppActiveContext();
                return EMPTY;
            }

            if (ServerError.is(error) && isStaleClientGraphQLResponseBody(error.bodyText)) {
                showStaleClientRefreshPrompt();
                return;
            }

            if (!hasStatusCode(error)) {
                showError(t('unknownErrorMessage'));
                return;
            }

            const errorWithStatus = error;

            if ([401, 403].includes(errorWithStatus.statusCode)) {
                const ctx = operation.getContext();
                const didAttemptRefresh = Boolean(ctx.__didAttemptAuthRefresh);

                // Best-effort silent refresh for cookie-based JWT sessions.
                if (!didAttemptRefresh) {
                    operation.setContext({ ...ctx, __didAttemptAuthRefresh: true });
                    return from(ensureFreshSession()).pipe(
                        mergeMap(({ ok }) => {
                            if (ok) {
                                return forward(operation);
                            }

                            // Refresh failed; treat this as unauthenticated.
                            if (
                                !['GetMe', 'GetFeatureFlags'].includes(operationName) &&
                                window.location.pathname !== '/login'
                            ) {
                                void signOut();
                            }
                            return throwError(() => error);
                        }),
                    );
                }

                // If we've already tried refreshing and still got 401/403, or we were using a one-time token,
                // fall back to signing out (except for unauthenticated bootstrapping queries).
                if (operationName === 'GetMe') {
                    return;
                }
                if (operationName === 'GetFeatureFlags') {
                    return;
                }
                if (window.location.pathname === '/login') {
                    return;
                }

                void signOut();
                return;
            }

            if (errorWithStatus.statusCode >= 400 && errorWithStatus.statusCode < 500) {
                Sentry.captureException(errorWithStatus);
            }

            showError(t('unknownErrorMessage'));
        });

        return new ApolloClient({
            cache,

            link: ApolloLink.from([
                networkStatusNotifierLink,
                errorLink,
                customHeadersLink,
                sentryBreadcrumbLink,
                httpLink,
            ]),

            defaultOptions: {
                watchQuery: {
                    errorPolicy: 'all',
                    fetchPolicy: 'cache-and-network',
                    nextFetchPolicy: (lastFetchPolicy) => {
                        if (
                            lastFetchPolicy === 'cache-and-network' ||
                            lastFetchPolicy === 'network-only'
                        ) {
                            return 'cache-first';
                        }
                        return lastFetchPolicy;
                    },
                },
            },
        });
    }, [cacheScopeKey, networkStatusNotifierLink, signOut]);

    return apolloClient;
}

export interface GraphqlProviderProps {
    networkStatusNotifierLink: ReturnType<typeof createNetworkStatusNotifier>['link'];
    children: React.ReactNode;
}

/**
 * Stable root Apollo provider for app-wide/session-scoped queries.
 *
 * This client intentionally does not rotate with client/vendor/off-resq context
 * changes. Lane-specific business data should live under ScopedGraphqlProvider.
 */
const GraphqlNetworkStatusLinkContext = React.createContext<
    ReturnType<typeof createNetworkStatusNotifier>['link'] | null
>(null);
const RootApolloClientContext = React.createContext<ApolloClient | null>(null);

export const GraphqlProvider = ({ networkStatusNotifierLink, children }: GraphqlProviderProps) => {
    const client = useApolloClient(networkStatusNotifierLink, 'root');
    return (
        <GraphqlNetworkStatusLinkContext.Provider value={networkStatusNotifierLink}>
            <RootApolloClientContext.Provider value={client}>
                <ApolloProvider client={client}>{children}</ApolloProvider>
            </RootApolloClientContext.Provider>
        </GraphqlNetworkStatusLinkContext.Provider>
    );
};

export interface ScopedGraphqlProviderProps {
    scopeKey: string;
    children: React.ReactNode;
}

/**
 * Lane-scoped Apollo provider for business data that is isolated by active
 * client/vendor/off-resq context.
 */
export const ScopedGraphqlProvider = ({ scopeKey, children }: ScopedGraphqlProviderProps) => {
    const networkStatusNotifierLink = React.useContext(GraphqlNetworkStatusLinkContext);

    if (!networkStatusNotifierLink) {
        throw new Error('ScopedGraphqlProvider must be mounted under GraphqlProvider.');
    }

    const client = useApolloClient(networkStatusNotifierLink, `scoped:${scopeKey}`);

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

/**
 * Rebind a subtree to the stable root Apollo client from within a scoped lane.
 *
 * Use this for session-scoped/profile surfaces that render inside a dashboard
 * shell but must read/write the same Apollo cache as SessionProvider.
 */
export const RootApolloBoundary = ({ children }: { children: React.ReactNode }) => {
    const client = React.useContext(RootApolloClientContext);

    if (!client) {
        throw new Error('RootApolloBoundary must be mounted under GraphqlProvider.');
    }

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export type Node = {
    id: string;
};

type Edge<TNode extends Node> = {
    node: Maybe<TNode>;
};

export type Connection<TNode extends Node> = {
    edges: Maybe<Edge<TNode>>[];
};

export type DeepPartialConnection<TNode extends Node> = DeepPartial<Connection<TNode>>;

export function getNodesFromConnection<TNode extends Node>(
    connection: Connection<TNode> | DeepPartialConnection<TNode> | null | undefined,
) {
    if (!connection) {
        return null;
    }
    const edges = (connection.edges ?? []) as (Edge<TNode> | null | undefined)[];
    return edges
        .map((edge) => edge?.node as TNode | null | undefined)
        .filter((node): node is TNode => Boolean(node));
}
